import * as Sentry from "@sentry/react";
import { Blac } from "blac-next";
import { FeatureResponse } from "@9amhealth/openapi";
import AssumedIdentityCubit from "src/state/AssumedIdentityCubit/AssumedIdentityCubit";
import { onAppActive } from "src/hybrid/appLifecycle";
import { featureFlags } from "./featureFlags";
import reportErrorSentry from "./reportErrorSentry";
import type { replayIntegration } from "./sentryReplayIntegration";

let replayWanted = false;
let replayLoading = false;
const hookedClients = new WeakSet<Sentry.BrowserClient>();

// Sentry.getReplay would pull the replay code into the main bundle.
const getReplay = () =>
  Sentry.getClient()?.getIntegrationByName<
    ReturnType<typeof replayIntegration>
  >("Replay");

// Restart on foreground, not on replayEnd: Sentry's own stops (send error,
// mutation limit) would loop, and replayEnd fires before stop() finishes.
const registerReplayHooks = (client: Sentry.BrowserClient): void => {
  if (hookedClients.has(client)) {
    return;
  }
  hookedClients.add(client);

  onAppActive(() => {
    if (replayWanted) {
      void startSentryReplay();
    }
  });
};

export const stopSentryReplay = async (): Promise<void> => {
  replayWanted = false;
  Sentry.setTag("replay_enabled", undefined);
  await getReplay()?.stop();
};

export const startSentryReplay = async (): Promise<void> => {
  const client = Sentry.getClient<Sentry.BrowserClient>();
  if (!client) {
    return;
  }

  try {
    await featureFlags.ready;
    const allowed =
      !Blac.getBloc(AssumedIdentityCubit).isActive &&
      featureFlags.getRemoteFlag(FeatureResponse.feature.SENTRY_REPLAY) ===
        true;
    if (!allowed) {
      await stopSentryReplay();
      return;
    }
    replayWanted = true;
    Sentry.setTag("replay_enabled", true);
    registerReplayHooks(client);

    const replay = getReplay();
    if (replay) {
      replay.start();
      return;
    }
    if (replayLoading) {
      return;
    }
    replayLoading = true;

    try {
      const { replayIntegration } = await import("./sentryReplayIntegration");
      if (!replayWanted) {
        return;
      }
      client.getOptions().replaysSessionSampleRate = 1;
      Sentry.addIntegration(
        replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true
        })
      );
    } finally {
      replayLoading = false;
    }
  } catch (error: unknown) {
    reportErrorSentry(error);
  }
};
